import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Plus, X, ChevronDown, Search } from 'lucide-react';
import axiosInstance from '../../../shared/services/api/axiosInstance';
import MultiWebsiteSelector from '../../../shared/components/analytics/MultiWebsiteSelector';

// Filter field definitions for User Journey
const FILTER_FIELDS = {
  // Text fields (single value)
  text: [],
  
  // User ID - special multiselect field (supports multiple User IDs)
  userId: [
    { id: 'userId', label: 'User ID' }
  ],
  
  // Date fields
  date: [
    { id: 'firstVisit', label: 'First Visit' },
    { id: 'lastVisit', label: 'Last Visit' }
  ],
  
  // Multiselect fields (dropdown selection only, no free text)
  multiselect: [
    { id: 'firstCountry', label: 'First Country', apiEndpoint: '/user-identities/options/first-countries' },
    { id: 'firstCity', label: 'First City', apiEndpoint: '/user-identities/options/first-cities' },
    { id: 'lastCountry', label: 'Last Country', apiEndpoint: '/user-identities/options/last-countries' },
    { id: 'lastCity', label: 'Last City', apiEndpoint: '/user-identities/options/last-cities' },
    { id: 'deviceType', label: 'Device Type', options: ['Windows', 'Macintosh', 'iOS', 'Android', 'Linux', 'Other'] },
    { id: 'browser', label: 'Browser', apiEndpoint: '/user-identities/options/browsers' },
    { id: 'operatingSystem', label: 'Operating System', apiEndpoint: '/user-identities/options/operating-systems' },
    { id: 'firstReferrer', label: 'First Referrer', options: ['direct', 'google', 'facebook', 'twitter', 'linkedin', 'instagram', 'youtube', 'reddit', 'tiktok', 'pinterest', 'other'] },
    { id: 'lastReferrer', label: 'Last Referrer', options: ['direct', 'google', 'facebook', 'twitter', 'linkedin', 'instagram', 'youtube', 'reddit', 'tiktok', 'pinterest', 'other'] },
    { id: 'walletType', label: 'Wallet Type', apiEndpoint: '/user-identities/options/wallet-types' },
    { id: 'walletChain', label: 'Wallet Chain', apiEndpoint: '/user-identities/options/wallet-chains' }
  ],
  
  // Boolean fields
  boolean: [
    { id: 'isBounceUser', label: 'Is Bounce User' },
    { id: 'isWeb3User', label: 'Is Web3 User' },
    { id: 'primaryWalletExists', label: 'Primary Wallet Exists' },
    { id: 'otherWalletExists', label: 'Other Wallet Exists' },
    { id: 'hasContractTransactions', label: 'Has Contract Transactions' },
    { id: 'hasName', label: 'Has Name' },
    { id: 'hasEmail', label: 'Has Email' },
    { id: 'hasPhone', label: 'Has Phone' },
    { id: 'hasTelegram', label: 'Has Telegram' },
    { id: 'hasDiscord', label: 'Has Discord' },
    { id: 'hasX', label: 'Has X (Twitter)' },
    { id: 'hasFarcaster', label: 'Has Farcaster' },
    { id: 'hasInstagram', label: 'Has Instagram' }
  ],
  
  // Numerical fields
  numerical: [
    { id: 'totalPageViews', label: 'Total Page Views' },
    { id: 'totalTimeSpent', label: 'Total Time Spent (seconds)' },
    { id: 'totalSessions', label: 'Total Sessions' }
  ]
};

// Operators by field type
const OPERATORS = {
  text: [
    { value: 'equals', label: 'equals' },
    { value: 'contains', label: 'contains' }
  ],
  userId: [
    { value: 'equals', label: 'is one of' }, // Supports multiple values
    { value: 'contains', label: 'contains' } // Single value search
  ],
  date: [
    { value: 'before', label: 'Is Before' },
    { value: 'after', label: 'Is After' },
    { value: 'between', label: 'Is Between' }
  ],
  multiselect: [
    { value: 'includes', label: 'includes' },
    { value: 'excludes', label: 'excludes' }
  ],
  boolean: [
    { value: 'is', label: 'is' }
  ],
  numerical: [
    { value: 'eq', label: 'Is Equal to' },
    { value: 'gt', label: 'Is Greater than' },
    { value: 'gte', label: 'Is Greater than equal to' },
    { value: 'lt', label: 'Is Less than' },
    { value: 'lte', label: 'Is Less than equal to' },
    { value: 'between', label: 'Is Between' }
  ]
};

// Field grouping for UI
const FIELD_GROUPS = [
  { label: 'Identifiers', fields: ['userId'] },
  { label: 'Visits', fields: ['firstVisit', 'lastVisit'] },
  { label: 'Engagement', fields: ['totalPageViews', 'totalTimeSpent', 'totalSessions'] },
  { label: 'Location', fields: ['firstCountry', 'firstCity', 'lastCountry', 'lastCity'] },
  { label: 'Referrer', fields: ['firstReferrer', 'lastReferrer'] },
  { label: 'Device & Browser', fields: ['deviceType', 'browser', 'operatingSystem'] },
  { label: 'Behavior', fields: ['isBounceUser'] },
  { label: 'Web3 & Wallet', fields: ['isWeb3User', 'primaryWalletExists', 'otherWalletExists', 'hasContractTransactions', 'walletType', 'walletChain'] },
  { label: 'Social Identity', fields: ['hasName', 'hasEmail', 'hasPhone', 'hasTelegram', 'hasDiscord', 'hasX', 'hasFarcaster', 'hasInstagram'] }
];

// MultiSelect Component for better UX
const MultiSelectDropdown = ({ 
  options, 
  selected, 
  onChange, 
  placeholder = "Select options...",
  loading = false,
  searchable = true
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  // Update dropdown position when opened
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const filteredOptions = searchable && searchTerm
    ? options.filter(opt => opt.toLowerCase().includes(searchTerm.toLowerCase()))
    : options;

  const toggleOption = (option) => {
    const isSelected = Array.isArray(selected) && selected.includes(option);
    if (isSelected) {
      onChange(selected.filter(v => v !== option));
    } else {
      onChange([...(Array.isArray(selected) ? selected : []), option]);
    }
  };

  const removeOption = (option, e) => {
    e.stopPropagation();
    onChange(selected.filter(v => v !== option));
  };

  const selectedArray = Array.isArray(selected) ? selected : [];

  // Debug logging
  useEffect(() => {
    if (isOpen) {
      console.log('[MultiSelectDropdown] State:', {
        isOpen,
        optionsCount: options?.length || 0,
        options,
        filteredCount: filteredOptions?.length || 0,
        loading
      });
    }
  }, [isOpen, options, filteredOptions, loading]);

  const dropdownContent = isOpen && (
    createPortal(
      <div
        ref={dropdownRef}
        className="fixed bg-white border border-[#DDE2EE] shadow-2xl max-h-[300px] overflow-hidden flex flex-col"
        style={{ borderRadius: 0 }}
        style={{
          position: 'fixed',
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`,
          width: `${dropdownPosition.width}px`,
          zIndex: 10000,
          maxHeight: '300px'
        }}
      >
        {/* Search input */}
        {searchable && (
          <div className="p-2 border-b border-[#DDE2EE] bg-white flex-shrink-0" style={{ borderRadius: 0 }}>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#475569]" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search options..."
                className="w-full pl-8 pr-3 py-2 text-sm border border-[#DDE2EE] focus:outline-none focus:ring-2 focus:ring-[#1d0c46]/20 focus:border-[#1d0c46] bg-white"
                style={{ borderRadius: 0, fontFamily: 'Raleway, sans-serif' }}
                onClick={(e) => e.stopPropagation()}
                onFocus={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>
          </div>
        )}

        {/* Options list */}
        <div className="overflow-y-auto bg-white flex-1" style={{ maxHeight: '240px' }}>
          {loading ? (
            <div className="p-4 text-center text-sm text-[#475569]">Loading options...</div>
          ) : filteredOptions.length > 0 ? (
            filteredOptions.map((option) => {
              const isSelected = selectedArray.includes(option);
              return (
                <label
                  key={option}
                  className="flex items-center px-4 py-2 hover:bg-gray-100 cursor-pointer transition-colors bg-white"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleOption(option);
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleOption(option)}
                    className="mr-3 h-4 w-4 text-[#1d0c46] focus:ring-[#1d0c46] border-gray-300 cursor-pointer"
                    style={{ accentColor: '#1d0c46' }}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <span className="text-sm text-[#475569] flex-1 select-none">{option}</span>
                </label>
              );
            })
          ) : (!options || options.length === 0) ? (
            <div className="p-4 text-center text-sm text-[#475569] bg-white">
              {loading ? 'Loading options...' : 'No options available'}
            </div>
          ) : (
            <div className="p-4 text-center text-sm text-[#475569] bg-white">
              No options found matching "{searchTerm}"
            </div>
          )}
        </div>

        {/* Selected count footer */}
        {selectedArray.length > 0 && (
          <div className="px-4 py-2 border-t border-gray-200 bg-gray-50 text-xs text-[#475569] flex-shrink-0">
            {selectedArray.length} {selectedArray.length === 1 ? 'option' : 'options'} selected
          </div>
        )}
      </div>,
      document.body
    )
  );

  return (
    <div className="relative w-full">
      {/* Selected chips / Trigger button */}
      <div
        ref={triggerRef}
        onClick={() => setIsOpen(!isOpen)}
        className="min-h-[40px] w-full px-3 py-2 border border-[#DDE2EE] bg-white cursor-pointer hover:border-gray-400 transition-colors flex items-center flex-wrap gap-2"
        style={{ borderRadius: 0, fontFamily: 'Raleway, sans-serif' }}
      >
        {selectedArray.length > 0 ? (
          <>
            {selectedArray.slice(0, 3).map((option) => (
              <span
                key={option}
                className="inline-flex items-center gap-1 px-2 py-1 bg-[#1d0c46]/10 text-[#1d0c46] text-xs"
                style={{ borderRadius: 0 }}
              >
                <span className="max-w-[150px] truncate">{option}</span>
                <button
                  onClick={(e) => removeOption(option, e)}
                  className="hover:bg-[#1d0c46]/20 p-0.5"
                  style={{ borderRadius: 0 }}
                  type="button"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
            {selectedArray.length > 3 && (
              <span className="text-xs text-[#475569] px-2">
                +{selectedArray.length - 3} more
              </span>
            )}
          </>
        ) : (
          <span className="text-[#475569] text-sm">{placeholder}</span>
        )}
        <ChevronDown className={`h-4 w-4 ml-auto text-[#475569] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </div>

      {/* Dropdown rendered via portal */}
      {dropdownContent}
    </div>
  );
};

const UserJourneyFilterBuilder = ({ filters, onChange, teamId, websiteId, dateRange, selectedSiteIds, onSiteChange }) => {
  const [localFilters, setLocalFilters] = useState(filters || []);
  const [fieldOptions, setFieldOptions] = useState({});
  const [loadingOptions, setLoadingOptions] = useState({});

  // Fetch options for multiselect fields from API endpoints
  useEffect(() => {
    const fetchFieldOptions = async () => {
      if (!teamId || !websiteId) return;
      
      const fieldsToFetch = FILTER_FIELDS.multiselect.filter(f => f.apiEndpoint);
      if (fieldsToFetch.length === 0) return;
      
      setLoadingOptions(prev => {
        const newState = { ...prev };
        fieldsToFetch.forEach(field => { newState[field.id] = true; });
        return newState;
      });
      
      // Fetch options for each field in parallel
      const fetchPromises = fieldsToFetch.map(async (field) => {
        try {
          const params = new URLSearchParams({
            team_id: teamId,
            website_id: websiteId
          });
          
          if (dateRange?.from) params.append('from', dateRange.from);
          if (dateRange?.to) params.append('to', dateRange.to);
          
          const response = await axiosInstance.get(`${field.apiEndpoint}?${params.toString()}`);
          
          if (response.data && response.data.success && Array.isArray(response.data.options)) {
            return { fieldId: field.id, options: response.data.options };
          } else if (response.data && Array.isArray(response.data.options)) {
            // Handle case where success field might be missing
            return { fieldId: field.id, options: response.data.options };
          }
          return { fieldId: field.id, options: [] };
        } catch (error) {
          console.error(`Error fetching options for ${field.id}:`, error);
          return { fieldId: field.id, options: [] };
        }
      });
      
      try {
        const results = await Promise.all(fetchPromises);
        const optionsMap = {};
        results.forEach(({ fieldId, options }) => {
          optionsMap[fieldId] = options;
        });
        setFieldOptions(optionsMap);
      } catch (error) {
        console.error('Error fetching field options:', error);
        setFieldOptions({});
      } finally {
        setLoadingOptions(prev => {
          const newState = { ...prev };
          fieldsToFetch.forEach(field => { newState[field.id] = false; });
          return newState;
        });
      }
    };

    fetchFieldOptions();
  }, [teamId, websiteId, dateRange]);

  const addFilter = () => {
    const newFilter = {
      id: `filter_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      field: null,
      operator: null,
      value: null,
      value2: null // For 'between' operator
    };
    const updated = [...localFilters, newFilter];
    setLocalFilters(updated);
    onChange(updated);
  };

  const updateFilter = (filterId, updates) => {
    const updated = localFilters.map(f => 
      f.id === filterId ? { ...f, ...updates } : f
    );
    setLocalFilters(updated);
    onChange(updated);
  };

  const removeFilter = (filterId) => {
    const updated = localFilters.filter(f => f.id !== filterId);
    setLocalFilters(updated);
    onChange(updated);
  };

  const getFieldType = (fieldId) => {
    if (FILTER_FIELDS.userId.find(f => f.id === fieldId)) return 'userId';
    if (FILTER_FIELDS.text.find(f => f.id === fieldId)) return 'text';
    if (FILTER_FIELDS.date.find(f => f.id === fieldId)) return 'date';
    if (FILTER_FIELDS.multiselect.find(f => f.id === fieldId)) return 'multiselect';
    if (FILTER_FIELDS.boolean.find(f => f.id === fieldId)) return 'boolean';
    if (FILTER_FIELDS.numerical.find(f => f.id === fieldId)) return 'numerical';
    return null;
  };

  const getFieldOptions = (fieldId) => {
    const field = [
      ...FILTER_FIELDS.userId,
      ...FILTER_FIELDS.text,
      ...FILTER_FIELDS.date,
      ...FILTER_FIELDS.multiselect,
      ...FILTER_FIELDS.boolean,
      ...FILTER_FIELDS.numerical
    ].find(f => f.id === fieldId);
    
    // Return static options if available
    if (field?.options) return field.options;
    
    // Return API-fetched options if available
    if (field?.apiEndpoint && fieldOptions[fieldId]) {
      return fieldOptions[fieldId];
    }
    
    return [];
  };

  // Sync local filters when parent filters change
  useEffect(() => {
    if (JSON.stringify(filters) !== JSON.stringify(localFilters)) {
      setLocalFilters(filters || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Get all fields grouped
  const getAllFields = () => {
    return [
      ...FILTER_FIELDS.userId,
      ...FILTER_FIELDS.text,
      ...FILTER_FIELDS.date,
      ...FILTER_FIELDS.multiselect,
      ...FILTER_FIELDS.boolean,
      ...FILTER_FIELDS.numerical
    ];
  };

  return (
    <div>
      {/* Website Selector - Modern card style */}
      {teamId && onSiteChange && (
        <div className="mb-6 p-5 bg-white border border-[#DDE2EE] shadow-sm" style={{ borderRadius: 0 }}>
          <label className="block text-sm font-semibold text-[#1d0c46] mb-3" style={{ fontFamily: 'Raleway, sans-serif' }}>
            Websites
          </label>
          <MultiWebsiteSelector
            selectedSiteIds={selectedSiteIds || []}
            onSiteChange={onSiteChange}
            teamId={teamId}
            className="w-full"
            placeholder="Select websites"
            showLabel={false}
          />
        </div>
      )}
      <div className="space-y-4">
        {localFilters.map((filter, index) => {
          const fieldType = filter.field ? getFieldType(filter.field) : null;
          const availableOperators = fieldType ? OPERATORS[fieldType] : [];
          const fieldOptionsList = filter.field ? getFieldOptions(filter.field) : [];
          
          return (
            <div 
              key={filter.id} 
              className="bg-white border border-[#DDE2EE] p-5 shadow-sm hover:shadow-md transition-all duration-200"
              style={{ borderRadius: 0 }}
            >
              <div className="flex items-start gap-3 flex-wrap">
                {/* Field Selector - Modern styled */}
                <div className="flex-1 min-w-[180px]">
                  <label className="block text-xs font-medium text-[#475569] mb-1.5" style={{ fontFamily: 'Raleway, sans-serif' }}>
                    Field
                  </label>
                  <select
                    value={filter.field || ''}
                    onChange={(e) => {
                      const selectedField = e.target.value;
                      const selectedFieldType = selectedField ? getFieldType(selectedField) : null;
                      // Auto-set operator for boolean fields
                      const newOperator = selectedFieldType === 'boolean' ? 'is' : null;
                      updateFilter(filter.id, { 
                        field: selectedField, 
                        operator: newOperator, 
                        value: null,
                        value2: null 
                      });
                    }}
                    className="w-full px-4 py-2.5 border border-[#DDE2EE] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1d0c46]/20 focus:border-[#1d0c46] transition-all duration-200 hover:border-gray-400"
                    style={{ fontFamily: 'Raleway, sans-serif', borderRadius: 0 }}
                  >
                <option value="">Select field...</option>
                {FIELD_GROUPS.map(group => {
                  const groupFields = getAllFields().filter(f => group.fields.includes(f.id));
                  if (groupFields.length === 0) return null;
                  return (
                    <optgroup key={group.label} label={group.label}>
                      {groupFields.map(field => (
                        <option key={field.id} value={field.id}>{field.label}</option>
                      ))}
                    </optgroup>
                  );
                })}
              </select>
                </div>

                {/* Operator Selector - Modern styled (skip for boolean fields) */}
                {filter.field && fieldType !== 'boolean' && (
                  <div className="flex-1 min-w-[140px]">
                    <label className="block text-xs font-medium text-[#475569] mb-1.5" style={{ fontFamily: 'Raleway, sans-serif' }}>
                      Operator
                    </label>
                    <select
                      value={filter.operator || ''}
                      onChange={(e) => {
                        updateFilter(filter.id, { 
                          operator: e.target.value, 
                          value: null,
                          value2: null 
                        });
                      }}
                      className="w-full px-4 py-2.5 border border-[#DDE2EE] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1d0c46]/20 focus:border-[#1d0c46] transition-all duration-200 hover:border-gray-400"
                      style={{ fontFamily: 'Raleway, sans-serif', borderRadius: 0 }}
                    >
                  <option value="">Select operator...</option>
                  {availableOperators.map(op => (
                    <option key={op.value} value={op.value}>{op.label}</option>
                  ))}
                </select>
                  </div>
              )}

                {/* Value Selector - Modern styled (show directly for boolean, after operator for others) */}
                {filter.field && (fieldType === 'boolean' || filter.operator) && (
                  <div className="flex-1 min-w-[240px]">
                    <label className="block text-xs font-medium text-[#475569] mb-1.5" style={{ fontFamily: 'Raleway, sans-serif' }}>
                      Value
                    </label>
                    <div className="space-y-2">
                      {/* User ID - supports multiple values */}
                      {fieldType === 'userId' && filter.operator === 'equals' && (
                        <div>
                          <textarea
                            value={
                              Array.isArray(filter.value) 
                                ? filter.value.join('\n') 
                                : (typeof filter.value === 'string' ? filter.value : '')
                            }
                            onChange={(e) => {
                              const text = e.target.value;
                              updateFilter(filter.id, { value: text });
                            }}
                            onBlur={(e) => {
                              const text = e.target.value;
                              const userIds = text
                                .split(/[\n,]+/)
                                .map(id => id.trim())
                                .filter(id => id.length > 0);
                              updateFilter(filter.id, { value: userIds.length > 0 ? userIds : null });
                            }}
                            placeholder="Enter User IDs (one per line or comma-separated)"
                            className="w-full px-4 py-2.5 border border-[#DDE2EE] bg-white text-xs min-h-[100px] font-mono resize-y focus:outline-none focus:ring-2 focus:ring-[#1d0c46]/20 focus:border-[#1d0c46] transition-all duration-200 hover:border-gray-400"
                            style={{ fontFamily: 'Raleway, sans-serif', borderRadius: 0 }}
                            rows={4}
                          />
                          {(() => {
                            const currentText = Array.isArray(filter.value) 
                              ? filter.value.join('\n')
                              : (typeof filter.value === 'string' ? filter.value : '');
                            const userIds = currentText
                              .split(/[\n,]+/)
                              .map(id => id.trim())
                              .filter(id => id.length > 0);
                            return userIds.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1.5">
                                {userIds.slice(0, 5).map((id, idx) => (
                                  <span key={idx} className="px-2.5 py-1 bg-[#1d0c46]/10 text-[#1d0c46] text-xs font-medium" style={{ borderRadius: 0, fontFamily: 'Raleway, sans-serif' }}>
                                    {id}
                                  </span>
                                ))}
                                {userIds.length > 5 && (
                                  <span className="px-2.5 py-1 bg-[#1d0c46]/10 text-[#1d0c46] text-xs font-medium" style={{ borderRadius: 0, fontFamily: 'Raleway, sans-serif' }}>
                                    +{userIds.length - 5} more
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      )}

                      {/* User ID - contains (single value search) */}
                      {fieldType === 'userId' && filter.operator === 'contains' && (
                        <input
                          type="text"
                          value={typeof filter.value === 'string' ? filter.value : ''}
                          onChange={(e) => updateFilter(filter.id, { value: e.target.value })}
                          placeholder="Search User ID..."
                          className="w-full px-4 py-2.5 border border-[#DDE2EE] bg-white text-xs font-mono focus:outline-none focus:ring-2 focus:ring-[#1d0c46]/20 focus:border-[#1d0c46] transition-all duration-200 hover:border-gray-400 placeholder-gray-400"
                          style={{ fontFamily: 'Raleway, sans-serif', borderRadius: 0 }}
                        />
                      )}

                      {/* Text input */}
                      {fieldType === 'text' && (
                        <input
                          type="text"
                          value={filter.value || ''}
                          onChange={(e) => updateFilter(filter.id, { value: e.target.value })}
                          placeholder="Enter value..."
                          className="w-full px-4 py-2.5 border border-[#DDE2EE] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1d0c46]/20 focus:border-[#1d0c46] transition-all duration-200 hover:border-gray-400 placeholder-gray-400"
                          style={{ fontFamily: 'Raleway, sans-serif', borderRadius: 0 }}
                        />
                      )}

                      {/* Date input */}
                      {fieldType === 'date' && filter.operator === 'between' && (
                        <div className="flex items-center gap-3">
                          <input
                            type="date"
                            value={filter.value || ''}
                            onChange={(e) => updateFilter(filter.id, { value: e.target.value })}
                            className="flex-1 px-4 py-2.5 border border-[#DDE2EE] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1d0c46]/20 focus:border-[#1d0c46] transition-all duration-200 hover:border-gray-400"
                            style={{ fontFamily: 'Raleway, sans-serif', borderRadius: 0 }}
                          />
                          <span className="text-sm text-[#475569] font-medium" style={{ fontFamily: 'Raleway, sans-serif' }}>and</span>
                          <input
                            type="date"
                            value={filter.value2 || ''}
                            onChange={(e) => updateFilter(filter.id, { value2: e.target.value })}
                            className="flex-1 px-4 py-2.5 border border-[#DDE2EE] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1d0c46]/20 focus:border-[#1d0c46] transition-all duration-200 hover:border-gray-400"
                            style={{ fontFamily: 'Raleway, sans-serif', borderRadius: 0 }}
                          />
                        </div>
                      )}

                      {fieldType === 'date' && filter.operator !== 'between' && (
                        <input
                          type="date"
                          value={filter.value || ''}
                          onChange={(e) => updateFilter(filter.id, { value: e.target.value })}
                          className="w-full px-4 py-2.5 border border-[#DDE2EE] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1d0c46]/20 focus:border-[#1d0c46] transition-all duration-200 hover:border-gray-400"
                          style={{ fontFamily: 'Raleway, sans-serif', borderRadius: 0 }}
                        />
                      )}

                      {/* Multiselect - Modern UI */}
                      {fieldType === 'multiselect' && (
                        <MultiSelectDropdown
                          options={fieldOptionsList || []}
                          selected={Array.isArray(filter.value) ? filter.value : []}
                          onChange={(selected) => updateFilter(filter.id, { value: selected })}
                          placeholder={loadingOptions[filter.field] ? "Loading options..." : fieldOptionsList.length === 0 ? "No options available" : "Select options..."}
                          loading={loadingOptions[filter.field]}
                          searchable={true}
                        />
                      )}

                      {/* Boolean */}
                      {fieldType === 'boolean' && (
                        <select
                          value={filter.value === true ? 'true' : filter.value === false ? 'false' : ''}
                          onChange={(e) => updateFilter(filter.id, { value: e.target.value === 'true' })}
                          className="w-full px-4 py-2.5 border border-[#DDE2EE] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1d0c46]/20 focus:border-[#1d0c46] transition-all duration-200 hover:border-gray-400"
                          style={{ fontFamily: 'Raleway, sans-serif', borderRadius: 0 }}
                        >
                          <option value="">Select...</option>
                          <option value="true">True</option>
                          <option value="false">False</option>
                        </select>
                      )}

                      {/* Numerical */}
                      {fieldType === 'numerical' && filter.operator === 'between' && (
                        <div className="flex items-center gap-3">
                          <input
                            type="number"
                            value={filter.value !== null && filter.value !== undefined ? filter.value : ''}
                            onChange={(e) => {
                              const val = e.target.value;
                              updateFilter(filter.id, { 
                                value: val === '' ? null : (isNaN(parseInt(val)) ? null : parseInt(val))
                              });
                            }}
                            placeholder="Min"
                            className="flex-1 px-4 py-2.5 border border-[#DDE2EE] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1d0c46]/20 focus:border-[#1d0c46] transition-all duration-200 hover:border-gray-400 placeholder-gray-400"
                            style={{ fontFamily: 'Raleway, sans-serif', borderRadius: 0 }}
                          />
                          <span className="text-sm text-[#475569] font-medium" style={{ fontFamily: 'Raleway, sans-serif' }}>and</span>
                          <input
                            type="number"
                            value={filter.value2 !== null && filter.value2 !== undefined ? filter.value2 : ''}
                            onChange={(e) => {
                              const val = e.target.value;
                              updateFilter(filter.id, { 
                                value2: val === '' ? null : (isNaN(parseInt(val)) ? null : parseInt(val))
                              });
                            }}
                            placeholder="Max"
                            className="flex-1 px-4 py-2.5 border border-[#DDE2EE] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1d0c46]/20 focus:border-[#1d0c46] transition-all duration-200 hover:border-gray-400 placeholder-gray-400"
                            style={{ fontFamily: 'Raleway, sans-serif', borderRadius: 0 }}
                          />
                        </div>
                      )}

                      {fieldType === 'numerical' && filter.operator !== 'between' && (
                        <input
                          type="number"
                          value={filter.value !== null && filter.value !== undefined ? filter.value : ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            updateFilter(filter.id, { 
                              value: val === '' ? null : (isNaN(parseInt(val)) ? null : parseInt(val))
                            });
                          }}
                          placeholder="Enter value"
                          className="w-full px-4 py-2.5 border border-[#DDE2EE] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1d0c46]/20 focus:border-[#1d0c46] transition-all duration-200 hover:border-gray-400 placeholder-gray-400"
                          style={{ fontFamily: 'Raleway, sans-serif', borderRadius: 0 }}
                        />
                      )}
                    </div>
                  </div>
                )}

                {/* Remove Button - Modern styled */}
                {filter.field && (
                  <div className="flex items-end">
                    <button
                      onClick={() => removeFilter(filter.id)}
                      className="p-2.5 hover:bg-red-50 text-[#475569] hover:text-red-600 transition-all duration-200 hover:scale-110 flex-shrink-0"
                      style={{ borderRadius: 0 }}
                      title="Remove filter"
                      type="button"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Add Filter Button - Modern styled */}
        <button
          onClick={addFilter}
          className="w-full flex items-center justify-center gap-2 px-5 py-3.5 border-2 border-dashed border-[#DDE2EE] hover:border-[#1d0c46]/30 bg-gray-50/50 hover:bg-[#1d0c46]/5 text-[#475569] hover:text-[#1d0c46] transition-all duration-200 font-medium"
          style={{ fontFamily: 'Raleway, sans-serif', borderRadius: 0 }}
          type="button"
        >
          <Plus className="h-5 w-5" />
          <span className="text-sm">Add Filter</span>
        </button>
      </div>
    </div>
  );
};

export default UserJourneyFilterBuilder;

